import { EnhancedLink } from '@/components/EnhancedLink';
import { Search } from '@/components/Search';
import { SearchMobile, SearchVariant } from '@/components/SearchMobile';
import { darkHeaderPages } from '@/constants';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { useTranslations } from '@/hooks/useTranslations';
import { NavDropdown } from '@/layout/Header/NavDropdown';
import { selectSearchActive, selectShowSidebar, setShowSidebar } from '@/store';
import { BuyButtonLink, MenuItem } from '@/types';
import { CommonTranslations } from '@/types/translations';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RWebShare } from 'react-web-share';
import { BurgerButton } from './BurgerButton';
import { BuyTicket } from './BuyTicket';
import styles from './Header.module.scss';
import { LocaleButton } from './LocaleButton';
import { NavbarButton } from './NavbarButton';
import { ProfileButton } from './ProfileButton';
// import { LocaleButton } from './LocaleButton';

type HeaderProps = {
  menu: MenuItem[];
  links: BuyButtonLink[];
  translations: CommonTranslations;
};

export function Header({ menu, links, translations }: HeaderProps) {
  const [headerHidden, setHeaderHidden] = useState(false);
  const [isActiveOnBurger, setActiveOnBurger] = useState(false);
  const { isTablet } = useAdaptiveWidth();
  const localTranslations = useTranslations();
  const { push, back, query, pathname } = useEnhancedRouter();
  const searchActive = useSelector(selectSearchActive);
  const showSidebar = useSelector(selectShowSidebar);
  const dispatch = useDispatch();
  const isDetailedPage =
    Object.keys(query).length || darkHeaderPages.includes(pathname);
  const isMainPage = pathname === '/';

  const actionsClassName = cn(styles.actions, {
    [styles.actionsActive]: searchActive,
  });

  const headerLinksClassName = cn(styles.main, {
    [styles.hidden]: headerHidden,
  });

  const headerClassName = cn(
    styles.header,
    {
      [styles.headerHidden]: headerHidden,
      [styles.headerHiddenActive]: searchActive,
    },
    'custom-header',
  );

  const handleNavbarButtonClick = (link: string) => {
    if (isMainPage && link === '/') return;
    dispatch(setShowSidebar(false));
    push(link);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10 && !isActiveOnBurger) {
        setHeaderHidden(true);
        return;
      }

      setHeaderHidden(false);
    };
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isActiveOnBurger]);

  const toggleHeader = () => {
    if (window.scrollY > 10) {
      setHeaderHidden((prev) => !prev);
      setActiveOnBurger((prev) => !prev);
    }
    return;
  };

  if (isTablet) {
    return (
      <header className={styles.mobile}>
        {isDetailedPage && !showSidebar ? (
          <div className={styles.topDetailed}>
            <div className={styles.topDetailedButtons}>
              <div className={styles.topDetailedButton} onClick={() => back()}>
                <img
                  loading="lazy"
                  src="/svg/arrow-big.svg"
                  alt="back"
                  className={styles.topDetailedIcon}
                />
              </div>
              <div className={styles.logoMobile}>
                <div className={styles.logoMobileWrapper}>
                  <img
                    loading="lazy"
                    src="/svg/logo-primary.svg"
                    alt="main"
                    className={styles.logoMobileIcon}
                    onClick={() => {
                      if (isMainPage) return;
                      push('/');
                    }}
                  />
                </div>
              </div>
              <div className={styles.topDetailedActions}>
                <RWebShare
                  data={{
                    url: window.location.href,
                  }}
                >
                  <div className={styles.topDetailedButton}>
                    <img
                      loading="lazy"
                      src="/svg/share.svg"
                      alt="share"
                      className={styles.topDetailedIcon}
                    />
                  </div>
                </RWebShare>
                <div className={styles.empty}></div>
                <SearchMobile variant={SearchVariant.Dark} />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.topWrapper}>
            <div className={styles.top}>
              {showSidebar && <LocaleButton />}
              <img
                loading="lazy"
                src={'/svg/logo-primary.svg'}
                width={36}
                height={48}
                alt="logo"
                className={cn(styles.logoMobileMain, {
                  [styles.withSidebar]: showSidebar,
                })}
                onClick={() => {
                  if (isMainPage) return;
                  push('/');
                }}
              />
              <SearchMobile variant={SearchVariant.Light} />
            </div>
          </div>
        )}
        <div className={styles.bottom}>
          <NavbarButton
            onClick={() => handleNavbarButtonClick('/')}
            img={pathname === '/' ? '/svg/home-dark.svg' : '/svg/home.svg'}
            text={localTranslations.main}
          />
          <NavbarButton
            onClick={() => handleNavbarButtonClick('/objects')}
            img={
              pathname === '/objects'
                ? '/svg/location-dark.svg'
                : '/svg/location.svg'
            }
            text={localTranslations.objects}
          />
          <BuyTicket links={links} />
          <NavbarButton
            onClick={() => handleNavbarButtonClick('/profile')}
            img={
              pathname === '/profile'
                ? '/svg/profile-dark.svg'
                : '/svg/profile.svg'
            }
            text={localTranslations.profile}
          />
          <NavbarButton
            onClick={() => dispatch(setShowSidebar(!showSidebar))}
            img={showSidebar ? '/svg/category-dark.svg' : '/svg/category.svg'}
            text={localTranslations.more}
          />
        </div>
      </header>
    );
  }

  return (
    <header className={headerClassName}>
      <div className={headerLinksClassName}>
        {!searchActive && (
          <>
            <EnhancedLink
              href={'/'}
              className={styles.logoMain}
              style={{ pointerEvents: isMainPage ? 'none' : 'all' }}
            >
              <img
                loading="lazy"
                src={'/svg/logo-primary.svg'}
                width={64}
                height={83}
                alt="logo"
                className={styles.logo}
              />
            </EnhancedLink>
            <div className={styles.links}>
              {menu
                .filter((el) => el.onlyMobile !== true)
                .map((item) => (
                  <NavDropdown
                    text={item.name}
                    key={item.id}
                    items={item.items}
                    icon={item.icon}
                    link={item.value}
                  />
                ))}
            </div>
          </>
        )}
      </div>
      <div className={actionsClassName}>
        {!searchActive && (
          <>
            <ProfileButton />
            <BurgerButton onCLick={toggleHeader} />
          </>
        )}
        <Search />
        <LocaleButton />
        <BuyTicket links={links} text={translations.ticketsTitle} />
      </div>
    </header>
  );
}
